import { useEffect, useRef, useState } from "react";
import { animate, useReducedMotion } from "framer-motion";

/**
 * Counter that rolls from its previous value to `value`.
 *
 * The earlier version gated the animation on `useInView`. That meant the
 * displayed number could stay at the initial `0` if the in-view trigger
 * never fired (small viewports, scroll already past, etc.) — leaving the
 * user looking at a wrong number.
 *
 * Now: animate as soon as we have a value and again whenever it changes.
 * If the user scrolled past before the animation ran, the final number is
 * correct, which is what matters.
 *
 * Respects prefers-reduced-motion by skipping the animation and showing
 * the final value directly.
 */
export function AnimatedNumber({
  value,
  durationMs = 1100,
}: {
  value: number;
  durationMs?: number;
}) {
  const prefersReduced = useReducedMotion();
  const [display, setDisplay] = useState(prefersReduced ? value : 0);
  const displayRef = useRef(display);

  useEffect(() => {
    if (prefersReduced) {
      displayRef.current = value;
      setDisplay(value);
      return;
    }

    if (displayRef.current === value) return;

    const controls = animate(displayRef.current, value, {
      duration: durationMs / 1000,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => {
        const rounded = Math.round(v);
        displayRef.current = rounded;
        setDisplay(rounded);
      },
    });
    return () => controls.stop();
  }, [value, durationMs, prefersReduced]);

  return <span aria-label={String(value)}>{display}</span>;
}
